// src/components/Account/AccountTools.jsx
import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const AccountTools = ({
  apiKeys = [],
  onCreateKey,
  onDeleteKey,
  onToggleKey,
  isProcessingApiKey,
  referralLinks = [],
  onCreateReferralLink,
  onDeleteReferralLink,
  isProcessingReferral,
  balance = 14850,
  onOpenWithdraw,
  onShowToast,
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  // Forms local state
  const [refCampaignInput, setRefCampaignInput] = useState('');
  const [apiKeyNameInput, setApiKeyNameInput] = useState('');

  // Unmasked keys set for eye toggle
  const [unmaskedKeyIds, setUnmaskedKeyIds] = useState(new Set());

  // Copy helper with Toast notification
  const handleCopy = (text, message) => {
    if (!text) return;

    const fallbackCopy = () => {
      try {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        if (onShowToast) onShowToast(message);
      } catch {
        if (onShowToast) onShowToast(message);
      }
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => {
          if (onShowToast) onShowToast(message);
        })
        .catch(() => {
          fallbackCopy();
        });
    } else {
      fallbackCopy();
    }
  };

  const toggleMask = (keyId) => {
    setUnmaskedKeyIds((prev) => {
      const next = new Set(prev);
      if (next.has(keyId)) next.delete(keyId);
      else next.add(keyId);
      return next;
    });
  };

  const handleCreateReferral = (e) => {
    e.preventDefault();
    const name = refCampaignInput.trim();
    if (!name || isProcessingReferral) return;
    onCreateReferralLink(name);
    setRefCampaignInput('');
    if (onShowToast) {
      onShowToast(
        isEn
          ? `Referral campaign "${name}" created!`
          : `Реферальная ссылка "${name}" создана!`
      );
    }
  };

  const handleCreateApiKey = (e) => {
    e.preventDefault();
    const name = apiKeyNameInput.trim();
    if (!name || isProcessingApiKey) return;
    onCreateKey(name);
    setApiKeyNameInput('');
    if (onShowToast) {
      onShowToast(
        isEn
          ? `API Key "${name}" generated!`
          : `API Ключ "${name}" успешно создан!`
      );
    }
  };

  const safeLinks = Array.isArray(referralLinks) ? referralLinks : [];
  const safeKeys = Array.isArray(apiKeys) ? apiKeys : [];

  return (
    <>
      {/* ==========================================================================
         2. РАЗДЕЛ РЕФЕРАЛОВ И БАЛАНСА
         ========================================================================== */}
      <section className="module-section" id="referralsSection">
        <div className="module-header">
          <div className="module-title-group">
            <h2 className="module-title">
              {isEn ? 'Referrals & Balance' : 'Рефералы и баланс'}
            </h2>
            <p className="module-desc">
              {isEn
                ? 'List of referral links with registrations and accrued profits.'
                : 'Список реферальных ссылок с количеством регистраций и прибылью.'}
            </p>
          </div>
        </div>

        {/* Капсульный бар баланса */}
        <div className="referral-banner-card">
          <div className="referral-balance-block">
            <div className="balance-icon-wrap">
              <svg viewBox="0 0 24 24">
                <rect width="20" height="14" x="2" y="5" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
            </div>
            <span className="balance-tag-label">
              {isEn ? 'Current Balance:' : 'Текущий баланс:'}
            </span>
            <div className="referral-balance-sum">
              <span>{(balance ?? 0).toLocaleString()}</span>
              <span className="balance-curr-symbol">₽</span>
            </div>
          </div>

          <div className="referral-actions-cluster">
            <button
              type="button"
              className="btn-pill-primary"
              onClick={onOpenWithdraw}
            >
              <svg viewBox="0 0 24 24">
                <rect width="20" height="14" x="2" y="5" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
              <span>{isEn ? 'Withdraw Funds' : 'Вывести средства'}</span>
            </button>
          </div>
        </div>

        {/* Панель создания новой реферальной ссылки */}
        <form className="create-item-pill-bar" onSubmit={handleCreateReferral}>
          <label htmlFor="refCampaignInput" className="create-item-label">
            {isEn ? 'New link:' : 'Новая ссылка:'}
          </label>
          <input
            type="text"
            id="refCampaignInput"
            className="create-item-input"
            placeholder={
              isEn
                ? 'Campaign name (e.g. telegram, forum)...'
                : 'Название кампании (например: telegram, forum)...'
            }
            value={refCampaignInput}
            onChange={(e) => setRefCampaignInput(e.target.value)}
            disabled={isProcessingReferral}
            autoComplete="off"
            required
          />
          <button
            type="submit"
            className="btn-pill-primary"
            disabled={isProcessingReferral || !refCampaignInput.trim()}
          >
            <svg viewBox="0 0 24 24">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>{isEn ? 'Create Link' : 'Создать ссылку'}</span>
          </button>
        </form>

        {/* Таблица реферальных ссылок */}
        <div className="cyber-table-card">
          {safeLinks.length === 0 ? (
            <div className="cyber-table-empty">
              <svg viewBox="0 0 24 24">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <p>
                {isEn
                  ? 'No referral links created yet. Create your first link above!'
                  : 'У вас пока нет созданных реферальных ссылок. Создайте первую выше!'}
              </p>
            </div>
          ) : (
            <>
              {/* Десктопная таблица */}
              <div className="table-responsive-wrapper table-desktop-view">
                <table className="cyber-data-table" id="referralsTable">
                  <thead>
                    <tr>
                      <th>{isEn ? 'Campaign' : 'Кампания'}</th>
                      <th>{isEn ? 'Referral Link' : 'Реферальная ссылка'}</th>
                      <th>{isEn ? 'Registrations' : 'Регистраций'}</th>
                      <th>{isEn ? 'Profit' : 'Прибыль'}</th>
                      <th style={{ textAlign: 'right' }}>
                        {isEn ? 'Actions' : 'Действия'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {safeLinks.map((ref) => {
                      const refName = ref.name || ref.title || 'Campaign';
                      const refCode = ref.code || String(ref.id || 'promo');
                      const fullUrl =
                        ref.url || `${window.location.origin}/r/${refCode}`;
                      const regsCount =
                        ref.registrations ?? ref.regs ?? ref.clicks ?? 0;
                      const profitVal =
                        ref.earnings ?? ref.profit ?? ref.totalEarnings ?? 0;

                      return (
                        <tr key={ref.id || refCode}>
                          <td>
                            <strong style={{ color: '#ffffff', fontWeight: 600 }}>
                              {refName}
                            </strong>
                          </td>
                          <td>
                            <div className="cell-link-box">
                              <span className="cell-link-text">{fullUrl}</span>
                              <button
                                type="button"
                                className="token-eye-btn"
                                onClick={() =>
                                  handleCopy(
                                    fullUrl,
                                    isEn
                                      ? 'Referral link copied!'
                                      : 'Реферальная ссылка скопирована!'
                                  )
                                }
                                title={isEn ? 'Copy link' : 'Копировать ссылку'}
                              >
                                <svg viewBox="0 0 24 24">
                                  <rect
                                    width="14"
                                    height="14"
                                    x="8"
                                    y="8"
                                    rx="2"
                                    ry="2"
                                  />
                                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                                </svg>
                              </button>
                            </div>
                          </td>
                          <td>
                            <span className="cell-numeric">
                              {regsCount.toLocaleString()}
                            </span>
                          </td>
                          <td>
                            <span className="cell-profit">
                              +{profitVal.toLocaleString()} ₽
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <div className="row-actions-group">
                              <button
                                type="button"
                                className="icon-action-btn"
                                onClick={() =>
                                  handleCopy(
                                    fullUrl,
                                    isEn
                                      ? 'Referral link copied!'
                                      : 'Ссылка скопирована!'
                                  )
                                }
                                title={isEn ? 'Copy' : 'Копировать'}
                              >
                                <svg viewBox="0 0 24 24">
                                  <rect
                                    width="14"
                                    height="14"
                                    x="8"
                                    y="8"
                                    rx="2"
                                    ry="2"
                                  />
                                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                className="icon-action-btn delete"
                                onClick={() => onDeleteReferralLink(ref.id)}
                                title={isEn ? 'Delete link' : 'Удалить ссылку'}
                              >
                                <svg viewBox="0 0 24 24">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Мобильный вид (карточки) */}
              <div className="mobile-cards-container">
                {safeLinks.map((ref) => {
                  const refName = ref.name || ref.title || 'Campaign';
                  const refCode = ref.code || String(ref.id || 'promo');
                  const fullUrl =
                    ref.url || `${window.location.origin}/r/${refCode}`;
                  const regsCount =
                    ref.registrations ?? ref.regs ?? ref.clicks ?? 0;
                  const profitVal =
                    ref.earnings ?? ref.profit ?? ref.totalEarnings ?? 0;

                  return (
                    <div key={ref.id || refCode} className="mobile-data-card">
                      <div className="mobile-data-card-header">
                        <strong style={{ color: '#ffffff', fontSize: '0.95rem' }}>
                          {refName}
                        </strong>
                        <span className="cell-profit">
                          +{profitVal.toLocaleString()} ₽
                        </span>
                      </div>
                      <div className="cell-link-box" style={{ maxWidth: '100%' }}>
                        <span className="cell-link-text">{fullUrl}</span>
                        <button
                          type="button"
                          className="token-eye-btn"
                          onClick={() =>
                            handleCopy(
                              fullUrl,
                              isEn ? 'Link copied!' : 'Ссылка скопирована!'
                            )
                          }
                        >
                          <svg viewBox="0 0 24 24">
                            <rect
                              width="14"
                              height="14"
                              x="8"
                              y="8"
                              rx="2"
                              ry="2"
                            />
                            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                          </svg>
                        </button>
                      </div>
                      <div className="mobile-data-card-row">
                        <span className="mobile-data-card-label">
                          {isEn ? 'Registrations' : 'Регистраций'}:
                        </span>
                        <span className="cell-numeric">{regsCount}</span>
                      </div>
                      <div
                        className="mobile-data-card-row"
                        style={{ justifyContent: 'flex-end', marginTop: 4 }}
                      >
                        <button
                          type="button"
                          className="btn-pill-secondary"
                          style={{
                            height: 32,
                            padding: '0 12px',
                            fontSize: '0.78rem',
                            color: 'var(--acc-accent-red)',
                            borderColor: 'rgba(255, 71, 87, 0.3)',
                          }}
                          onClick={() => onDeleteReferralLink(ref.id)}
                        >
                          {isEn ? 'Delete' : 'Удалить'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ==========================================================================
         3. РАЗДЕЛ API КЛЮЧЕЙ
         ========================================================================== */}
      <section className="module-section" id="apiKeysSection">
        <div className="module-header">
          <div className="module-title-group">
            <h2 className="module-title">
              <span className="title-api-match">API</span>{' '}
              {isEn ? 'Keys' : 'Ключи'}
            </h2>
            <p className="module-desc">
              {isEn
                ? 'Programmatic access to server databases, RCON audits, and automated checks.'
                : 'Программный доступ к базам серверов, RCON-аудиту и автоматическому чекеру.'}
            </p>
          </div>
        </div>

        {/* Панель генерации нового ключа */}
        <form className="create-item-pill-bar" onSubmit={handleCreateApiKey}>
          <label htmlFor="apiKeyNameInput" className="create-item-label">
            {isEn ? 'New API key:' : 'Новый API ключ:'}
          </label>
          <input
            type="text"
            id="apiKeyNameInput"
            className="create-item-input"
            placeholder={
              isEn
                ? 'Service name (e.g. Discord Bot, Auto Checker)...'
                : 'Название сервиса (например: Discord Bot, Auto Checker)...'
            }
            value={apiKeyNameInput}
            onChange={(e) => setApiKeyNameInput(e.target.value)}
            disabled={isProcessingApiKey}
            autoComplete="off"
            required
          />
          <button
            type="submit"
            className="btn-pill-primary"
            disabled={isProcessingApiKey || !apiKeyNameInput.trim()}
          >
            <svg viewBox="0 0 24 24">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>{isEn ? 'Generate Key' : 'Сгенерировать ключ'}</span>
          </button>
        </form>

        {/* Таблица API Ключей */}
        <div className="cyber-table-card">
          {safeKeys.length === 0 ? (
            <div className="cyber-table-empty">
              <svg viewBox="0 0 24 24">
                <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <p>
                {isEn
                  ? 'No active API keys found. Generate a new key above!'
                  : 'Активных API ключей пока нет. Сгенерируйте новый ключ выше.'}
              </p>
            </div>
          ) : (
            <>
              {/* Десктопная таблица */}
              <div className="table-responsive-wrapper table-desktop-view">
                <table className="cyber-data-table" id="apiKeysTable">
                  <thead>
                    <tr>
                      <th>{isEn ? 'Service / Name' : 'Сервис / Название'}</th>
                      <th>{isEn ? 'API Token' : 'API Токен'}</th>
                      <th>{isEn ? 'Created' : 'Создан'}</th>
                      <th>{isEn ? 'Requests / Month' : 'Запросов / Мес'}</th>
                      <th style={{ textAlign: 'right' }}>
                        {isEn ? 'Actions' : 'Действия'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {safeKeys.map((key) => {
                      const keyName = key.name || 'API Key';
                      const rawToken = key.token || key.key || '';
                      const isUnmasked = unmaskedKeyIds.has(key.id);

                      const displayToken = isUnmasked
                        ? rawToken
                        : rawToken.length > 12
                        ? rawToken.slice(0, 8) +
                          '••••••••••••' +
                          rawToken.slice(-4)
                        : rawToken
                        ? '••••••••••••••••••••'
                        : 'sb_live_••••••••••••';

                      const createdDate =
                        key.created || (key.createdAt ? new Date(key.createdAt).toLocaleDateString(isEn ? 'en-US' : 'ru-RU') : '—');
                      const requestsText =
                        key.requestCount !== undefined ? `${key.requestCount.toLocaleString()} / 50 000` : (key.requests ? String(key.requests) : '0 / 50 000');

                      return (
                        <tr key={key.id}>
                          <td>
                            <strong style={{ color: '#ffffff', fontWeight: 600 }}>
                              {keyName}
                            </strong>
                          </td>
                          <td>
                            <div className="token-masked-capsule">
                              <span className="token-text">{displayToken}</span>
                              <button
                                type="button"
                                className="token-eye-btn"
                                onClick={() => toggleMask(key.id)}
                                title={
                                  isUnmasked
                                    ? isEn
                                      ? 'Hide token'
                                      : 'Скрыть токен'
                                    : isEn
                                    ? 'Show token'
                                    : 'Показать токен'
                                }
                              >
                                {isUnmasked ? (
                                  <svg viewBox="0 0 24 24">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                  </svg>
                                ) : (
                                  <svg viewBox="0 0 24 24">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </td>
                          <td>
                            <span
                              style={{
                                fontFamily: 'var(--acc-font-mono)',
                                fontSize: '0.82rem',
                                color: 'rgba(255, 255, 255, 0.6)',
                              }}
                            >
                              {createdDate}
                            </span>
                          </td>
                          <td>
                            <span
                              style={{
                                fontFamily: 'var(--acc-font-mono)',
                                fontSize: '0.82rem',
                                color: '#ffffff',
                              }}
                            >
                              {requestsText}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <div className="row-actions-group">
                              <button
                                type="button"
                                className="icon-action-btn"
                                onClick={() =>
                                  handleCopy(
                                    rawToken,
                                    isEn
                                      ? 'API Key copied to clipboard!'
                                      : 'API Ключ скопирован в буфер!'
                                  )
                                }
                                title={isEn ? 'Copy token' : 'Копировать токен'}
                              >
                                <svg viewBox="0 0 24 24">
                                  <rect
                                    width="14"
                                    height="14"
                                    x="8"
                                    y="8"
                                    rx="2"
                                    ry="2"
                                  />
                                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                className="icon-action-btn delete"
                                onClick={() => onDeleteKey(key.id)}
                                title={
                                  isEn
                                    ? 'Revoke / Delete key'
                                    : 'Отозвать / Удалить ключ'
                                }
                              >
                                <svg viewBox="0 0 24 24">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Мобильный вид (карточки) */}
              <div className="mobile-cards-container">
                {safeKeys.map((key) => {
                  const keyName = key.name || 'API Key';
                  const rawToken = key.token || key.key || '';
                  const isUnmasked = unmaskedKeyIds.has(key.id);

                  const displayToken = isUnmasked
                    ? rawToken
                    : rawToken.length > 12
                    ? rawToken.slice(0, 8) + '••••••••••••' + rawToken.slice(-4)
                    : rawToken
                    ? '••••••••••••••••••••'
                    : 'sb_live_••••••••••••';

                  const createdDate =
                    key.created || (key.createdAt ? new Date(key.createdAt).toLocaleDateString(isEn ? 'en-US' : 'ru-RU') : '—');
                  const requestsText =
                    key.requestCount !== undefined ? `${key.requestCount.toLocaleString()} / 50 000` : (key.requests ? String(key.requests) : '0 / 50 000');

                  return (
                    <div key={key.id} className="mobile-data-card">
                      <div className="mobile-data-card-header">
                        <strong style={{ color: '#ffffff', fontSize: '0.95rem' }}>
                          {keyName}
                        </strong>
                        <span
                          style={{
                            fontFamily: 'var(--acc-font-mono)',
                            fontSize: '0.78rem',
                            color: 'rgba(255, 255, 255, 0.5)',
                          }}
                        >
                          {createdDate}
                        </span>
                      </div>
                      <div
                        className="token-masked-capsule"
                        style={{ maxWidth: '100%' }}
                      >
                        <span
                          className="token-text"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {displayToken}
                        </span>
                        <button
                          type="button"
                          className="token-eye-btn"
                          onClick={() => toggleMask(key.id)}
                        >
                          {isUnmasked ? (
                            <svg viewBox="0 0 24 24">
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                              <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                          )}
                        </button>
                      </div>
                      <div className="mobile-data-card-row">
                        <span className="mobile-data-card-label">
                          {isEn ? 'Requests' : 'Запросов'}:
                        </span>
                        <span
                          style={{
                            fontFamily: 'var(--acc-font-mono)',
                            fontSize: '0.82rem',
                            color: '#ffffff',
                          }}
                        >
                          {requestsText}
                        </span>
                      </div>
                      <div
                        className="mobile-data-card-row"
                        style={{ justifyContent: 'flex-end', gap: 8, marginTop: 4 }}
                      >
                        <button
                          type="button"
                          className="btn-pill-secondary"
                          style={{
                            height: 32,
                            padding: '0 12px',
                            fontSize: '0.78rem',
                          }}
                          onClick={() =>
                            handleCopy(
                              rawToken,
                              isEn
                                ? 'API Key copied!'
                                : 'API Ключ скопирован!'
                            )
                          }
                        >
                          {isEn ? 'Copy' : 'Копировать'}
                        </button>
                        <button
                          type="button"
                          className="btn-pill-secondary"
                          style={{
                            height: 32,
                            padding: '0 12px',
                            fontSize: '0.78rem',
                            color: 'var(--acc-accent-red)',
                            borderColor: 'rgba(255, 71, 87, 0.3)',
                          }}
                          onClick={() => onDeleteKey(key.id)}
                        >
                          {isEn ? 'Delete' : 'Удалить'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default AccountTools;